import { forwardRef, type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';

/**
 * Con trượt kính — nền của mục đang chọn, trượt sang mục mới (`.glass-slider`).
 *
 * Dùng chung cho thanh điều hướng và các dải tab kiểu kính (`TabsList
 * variant="glass"`, cụm Danh sách/Lịch của Việc của tôi): một chỗ đo, một chỗ
 * quyết khi nào nhún, nên hai nơi không trôi khỏi nhau.
 *
 * Nó bám theo **thuộc tính** của mục đang chọn (`activeSelector`), không theo cú
 * bấm: `data-status="active"` của router, `data-state="active"` của Radix,
 * `aria-current="true"` của nút tự viết. Vào thẳng bằng URL, bấm Back, hay đổi
 * tab bằng phím mũi tên thì viên vẫn đứng đúng chỗ — `MutationObserver` thấy
 * thuộc tính đổi và đặt lại.
 *
 * Không mục nào khớp thì viên mờ đi tại chỗ thay vì trượt về đầu hàng: một viên
 * đứng dưới "Tổng quan" khi người dùng đang ở Cài đặt là nói sai họ đang ở đâu.
 *
 * Đặt cả bốn cạnh (`top`/`left`/`width`/`height`) và **mượn độ bo** của mục
 * đang chọn, nên cùng một viên chạy được ngang (thanh, dải tab — mục tròn hết
 * cỡ) lẫn dọc (thanh bên dự án — hàng bo 16px, cao 70px). Viên bo tròn hết cỡ
 * đè lên một hàng góc 16px là một viên thuốc lòi ra ngoài thẻ.
 *
 * ⚠️ `container` phải là phần tử **có định vị** (`relative`) — gốc toạ độ của
 * viên. Và các mục phải
 * **có định vị** nữa (`relative`): viên là `absolute`, và phần tử có định vị vẽ
 * đè lên mọi phần tử không định vị bất kể thứ tự trong DOM — thiếu nó là viên
 * phủ lên chữ.
 */
export function useGlassSlider(
  containerRef: RefObject<HTMLElement | null>,
  activeSelector: string,
): RefObject<HTMLSpanElement | null> {
  const sliderRef = useRef<HTMLSpanElement | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const slider = sliderRef.current;
    if (!container || !slider) return;

    let settle: ReturnType<typeof setTimeout> | undefined;
    const place = (): void => {
      const active = container.querySelector<HTMLElement>(activeSelector);
      if (!active) {
        slider.style.opacity = '0';
        return;
      }
      const { left, top, width, height } = boxIn(container, active);
      /*
        Dẹt ra lúc trượt rồi tròn lại — chỉ khi nó **đi** từ một mục đang hiện
        sang mục khác. Lần đặt đầu tiên, hay lúc đo lại cùng chỗ khi đổi cỡ cửa
        sổ, mà cũng nhún thì dải giật mỗi lần kéo cửa sổ.
      */
      if (
        slider.style.opacity === '1' &&
        (slider.style.left !== left || slider.style.top !== top)
      ) {
        slider.dataset.moving = '';
        clearTimeout(settle);
        settle = setTimeout(() => delete slider.dataset.moving, 240);
      }
      slider.style.left = left;
      slider.style.top = top;
      slider.style.borderRadius = getComputedStyle(active).borderRadius;
      slider.style.width = width;
      slider.style.height = height;
      slider.style.opacity = '1';
    };

    place();

    /*
      `class` nằm trong danh sách vì bề rộng mục đổi theo lớp — thanh điều hướng
      bỏ chữ giữ icon bằng `sr-only`. `data-moving` và `style` của chính viên thì
      không, nên viên tự đổi mình không kích lại vòng đo.
    */
    const mutations = new MutationObserver(place);
    mutations.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-state', 'data-status', 'aria-current', 'data-active', 'class'],
    });
    const resize = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(place);
    resize?.observe(container);

    return () => {
      mutations.disconnect();
      resize?.disconnect();
      clearTimeout(settle);
    };
  }, [containerRef, activeSelector]);

  return sliderRef;
}

/**
 * Hộp của `el` trong hệ toạ độ của `container`, **số lẻ chính xác**.
 *
 * ⚠️ Không dùng `offsetTop`/`offsetHeight`: chúng làm tròn về số nguyên, và khi
 * hàng cao lẻ (68,8px — tuỳ phông của máy) viên thò quá đáy hàng cuối chưa tới
 * 1px. Trong một danh sách cuộn được (thanh bên dự án), chừng ấy đủ để trình
 * duyệt bật thanh cuộn dọc cho một danh sách ba dự án — người dùng báo
 * 2026-09-28, và nó không lặp lại trên máy có phông cho chiều cao tròn.
 *
 * `getBoundingClientRect` thì lẻ nhưng tính cả `scale` của phần tử — nút kính
 * co còn 0.96 lúc bị nhấn (`.glass-hover:active`), và tab của Radix đổi ngay
 * lúc nhấn chuột. Nên cỡ chia lại cho `scale`, vị trí lấy từ **tâm** (co giãn
 * quanh tâm không dời tâm).
 */
function boxIn(
  container: HTMLElement,
  el: HTMLElement,
): { left: string; top: string; width: string; height: string } {
  const c = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const [sx = 1, sy = sx] = getComputedStyle(el)
    .scale.split(' ')
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  const width = r.width / sx;
  const height = r.height / sy;
  const left =
    r.left + r.width / 2 - width / 2 - c.left - container.clientLeft + container.scrollLeft;
  const top = r.top + r.height / 2 - height / 2 - c.top - container.clientTop + container.scrollTop;
  return { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` };
}

/** Phần tử của con trượt — đặt làm con **đầu tiên** để các mục vẽ đè lên nó. */
export const GlassSlider = forwardRef<HTMLSpanElement>((_props, ref): ReactNode => (
  <span ref={ref} aria-hidden className="glass-slider opacity-0" />
));
GlassSlider.displayName = 'GlassSlider';
